import {
  ECareerLandingPageTabs,
  ECommonContentLayout,
  thunkActionLoading,
} from '@configs'
import { zodResolver } from '@hookform/resolvers/zod'
import { useAppDispatch, useAppSelector } from '@redux'
import { message } from 'antd'
import { t } from 'i18next'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { ECareerBannerType } from 'src/interfaces/career-landing-management'
import { IMediaItem } from 'src/interfaces/gallery'
import {
  createCareerBannerTopAction,
  getCareerBannerTopAction,
} from 'src/redux/actions/career-landing-management'
import { bannerTopSchema, IBannerTop } from './bannerSchema'

interface IAboutUsPageTabItemProps {
  activeTab?: string
}

export const useBannerTop = ({ activeTab }: IAboutUsPageTabItemProps) => {
  const [isModalLoading, setIsModalLoading] = useState(false)
  const dispatch = useAppDispatch()

  const [isChange, setIsChange] = useState(false)

  const [showModalSelectMedia, setShowModalSelectMedia] = useState(false)
  const [selectedField, setSelectedField] = useState<
    'desktop' | 'mobile' | null
  >(null)
  const onChangeDesktopRef = useRef<any>(null)
  const onChangeMobileRef = useRef<any>(null)

  const createBannerTopActionLoading = useAppSelector(
    (state) =>
      state.careerLanding.loadings[thunkActionLoading.CREATE_BANNER_TOP_LOADING]
  )

  const getBannerTopActionLoading = useAppSelector(
    (state) =>
      state.careerLanding.loadings[thunkActionLoading.GET_BANNER_TOP_LOADING]
  )

  const bannerTop = useAppSelector((state) => state.careerLanding.bannerTop)

  const [imageSrc, setImageSrc] = useState(bannerTop?.media?.url ?? '')
  const [imageMobileSrc, setImageMobileSrc] = useState(
    bannerTop?.mediaMobile?.url ?? ''
  )

  const isDisabledSaveButton = useMemo(() => {
    return createBannerTopActionLoading || false
  }, [createBannerTopActionLoading])

  const { handleSubmit, control, setValue, getValues } = useForm<IBannerTop>({
    defaultValues: {
      title: '',
      titleInDutch: '',
      subTitle: '',
      subTitleInDutch: '',
      mediaId: undefined,
      mediaMobileId: undefined,
      textColor: '',
      layout: ECommonContentLayout.LAYOUT_ONE,
      type: ECareerBannerType.top,
    },
    resolver: zodResolver(bannerTopSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  })
  const onSubmit = async (data: IBannerTop) => {
    try {
      const responseCreateLanding = await dispatch(
        createCareerBannerTopAction(data as any)
      ).unwrap()

      if (responseCreateLanding.success) {
        message.success({
          content: responseCreateLanding?.message ?? t('common:save_success'),
        })
      }
    } catch (error: any) {
      message.error({
        content:
          error?.message ?? t('error:some_thing_went_wrong_please_try_again'),
      })
    }
  }

  const onSave = () => {
    handleSubmit(onSubmit)()
    setIsChange(false)
  }

  const onCloseModalSelectMedia = () => {
    setShowModalSelectMedia(false)
  }

  const onSelectMedia = (data: IMediaItem) => {
    if (selectedField === 'desktop') {
      setImageSrc(data?.baseUrl ?? '')
      setValue('mediaId', data?.id ?? 0)
      onChangeDesktopRef.current(data?.id ?? 0)
    } else if (selectedField === 'mobile') {
      setImageMobileSrc(data?.baseUrl ?? '')
      setValue('mediaMobileId', data?.id ?? 0)
      onChangeMobileRef.current(data?.id ?? 0)
    }
    setShowModalSelectMedia(false)
    setIsChange(true)
  }

  useEffect(() => {
    if (activeTab === ECareerLandingPageTabs.BANNER) {
      const getBannerTop = async () => {
        try {
          const responseGetLanding = await dispatch(
            getCareerBannerTopAction(ECareerBannerType.top)
          ).unwrap()

          if (responseGetLanding.success) {
            setIsChange(false)
          }
        } catch (error: any) {
          message.error({
            content:
              error.message ??
              t('error:some_thing_went_wrong_please_try_again'),
          })
        }
      }
      getBannerTop()
    }
  }, [activeTab, dispatch, setValue])

  useEffect(() => {
    if (bannerTop) {
      if (Object.keys(bannerTop).length) {
        setValue('title', bannerTop?.title)
        setValue('titleInDutch', bannerTop?.titleInDutch)
        setValue('subTitle', bannerTop?.subTitle ?? '')
        setValue('subTitleInDutch', bannerTop?.subTitleInDutch ?? '')
        setValue('mediaDescription', bannerTop?.mediaDescription ?? '')
        setValue(
          'mediaDescriptionInDutch',
          bannerTop?.mediaDescriptionInDutch ?? ''
        )
        setValue('mediaId', bannerTop?.media?.id)
        setValue('mediaMobileId', bannerTop?.mediaMobile?.id)
        setValue('textColor', bannerTop?.metaData?.textColor)
        setValue('layout', bannerTop?.metaData?.layout.toString())
        setImageSrc(bannerTop?.media?.url ?? '')
        setImageMobileSrc(bannerTop?.mediaMobile?.url ?? '')
      }
    }
  }, [bannerTop])

  return {
    getBannerTopActionLoading,
    control,
    setIsChange,
    onChangeDesktopRef,
    setValue,
    setImageSrc,
    setSelectedField,
    setShowModalSelectMedia,
    imageSrc,
    getValues,
    isModalLoading,
    setIsModalLoading,
    onChangeMobileRef,
    isDisabledSaveButton,
    imageMobileSrc,

    onSave,
    isChange,
    showModalSelectMedia,
    onCloseModalSelectMedia,
    onSelectMedia,
  }
}
